import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Play, Pause, RotateCcw, Timer } from 'lucide-react';

const LOCAL_STORAGE_KEY = 'stopwatch_state_v1';

const Stopwatch = () => {
  const [isActive, setIsActive] = useState(false);
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [elapsedBeforeStart, setElapsedBeforeStart] = useState(0);
  const [displayMs, setDisplayMs] = useState(0);

  const intervalRef = useRef(null);

  const persist = (next = {}) => {
    const state = {
      isActive,
      startTimestamp,
      elapsedBeforeStart,
      ...next,
    };
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (typeof saved.isActive === 'boolean') setIsActive(saved.isActive);
        if (typeof saved.elapsedBeforeStart === 'number') setElapsedBeforeStart(saved.elapsedBeforeStart);
        if (typeof saved.startTimestamp === 'number') setStartTimestamp(saved.startTimestamp);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const computeDisplay = () => {
      const now = Date.now();
      const base = elapsedBeforeStart || 0;
      const runningExtra = isActive && startTimestamp ? (now - startTimestamp) : 0;
      setDisplayMs(base + runningExtra);
    };

    computeDisplay();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(computeDisplay, 200);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, startTimestamp, elapsedBeforeStart]);

  const handleStart = () => {
    if (isActive) return;
    const now = Date.now();
    setIsActive(true);
    setStartTimestamp(now);
    persist({ isActive: true, startTimestamp: now, elapsedBeforeStart });
  };

  const handleStop = () => {
    if (!isActive) return;
    const now = Date.now();
    const sessionElapsed = startTimestamp ? now - startTimestamp : 0;
    const nextElapsed = (elapsedBeforeStart || 0) + sessionElapsed;
    setIsActive(false);
    setElapsedBeforeStart(nextElapsed);
    setStartTimestamp(null);
    persist({ isActive: false, startTimestamp: null, elapsedBeforeStart: nextElapsed });
  };

  const handleReset = () => {
    setIsActive(false);
    setElapsedBeforeStart(0);
    setStartTimestamp(null);
    setDisplayMs(0);
    persist({ isActive: false, startTimestamp: null, elapsedBeforeStart: 0 });
  };

  const formatTime = (totalMs) => {
    const totalSeconds = Math.floor(totalMs / 1000);
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);
    const two = (n) => `0${n}`.slice(-2);
    if (hours > 0) {
      return `${hours}:${two(minutes)}:${two(seconds)}`;
    }
    return `${two(minutes)}:${two(seconds)}`;
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 shadow-2xl">
      <Card className="p-3 border bg-card/95 backdrop-blur backdrop-saturate-150 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Timer className={`w-5 h-5 ${isActive ? 'text-emerald-500 animate-pulse' : 'text-muted-foreground'}`} />
            <div className="text-2xl font-mono font-bold tracking-wider">
              {formatTime(displayMs)}
            </div>
            {isActive && (
              <Badge variant="success" className="text-[10px] px-1.5 py-0 h-4">
                LIVE
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {!isActive ? (
              <Button
                size="icon"
                variant="default"
                onClick={handleStart}
                title="Start Match Timer"
                className="h-8 w-8 rounded-lg shadow-sm"
              >
                <Play className="w-4 h-4 fill-current ml-0.5" />
              </Button>
            ) : (
              <Button
                size="icon"
                variant="destructive"
                onClick={handleStop}
                title="Pause Match Timer"
                className="h-8 w-8 rounded-lg shadow-sm"
              >
                <Pause className="w-4 h-4 fill-current" />
              </Button>
            )}

            <Button
              size="icon"
              variant="outline"
              onClick={handleReset}
              title="Reset Timer"
              className="h-8 w-8 rounded-lg"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Stopwatch;